
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Trash, Plus, MapPin, Pencil, Search, MapPinHouseIcon, Loader2 } from 'lucide-react';
import { useToast } from "@/hooks/use-toast";
import { useAuth } from '@/contexts/AuthContext';
import AddLocationForm from '@/components/agenda/AddLocationForm';
import EditLocationForm from '@/components/agenda/EditLocationForm';
import { useLanguage } from '@/contexts/LanguageContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { useIsMobile } from '@/hooks/use-mobile';
import { BASE_URL } from '@/config/api';

interface Location {
  id: string;
  name: string;
  isOwned: boolean;
}

interface LocationsManagerProps {
  small?: boolean;
}

const LocationsManager: React.FC<LocationsManagerProps> = ({ small = false }) => {
  const { token, refreshUserData } = useAuth();
  const [locations, setLocations] = useState<Location[]>([]);
  const [isAddingLocation, setIsAddingLocation] = useState(false);
  const [editingLocation, setEditingLocation] = useState<Location | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const { toast } = useToast();
  const { t } = useLanguage();
  const isMobile = useIsMobile();
  const [locationToDelete, setLocationToDelete] = useState<Location | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  useEffect(() => {
    fetchLocations();
  }, [token]);

  const fetchLocations = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/api/locations`, {
        headers: {
          'Accept' : 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      
      if (response.ok && data.status === 1) {
        setLocations(data.locations || []);
      } else {
        /*toast({
          title: "Failed to fetch locations",
          description: data.message || "There was an error loading the locations.",
          variant: "destructive"
        });*/
        setLocations([]); 
      }
    } catch (error) {
      console.error('Error fetching locations:', error);
      toast({
        title: "Failed to fetch locations",
        description: "There was an error loading the locations list.",
        variant: "destructive"
      });
      setLocations([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddLocation = async (name: string) => {
    if (!name.trim()) {
      toast({
        title: "Location name required",
        description: "Please enter a name for the location",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/api/add_location`, {
        method: 'POST',
        headers: {
          'Accept' : 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ name: name.trim() })
      });

      if (!response.ok) {
        throw new Error('Failed to add location');
      }

      // Refresh locations
      fetchLocations();
      
      toast({
        title: "Location added",
        description: `${name} has been added to your locations list`
      });
      
      setIsAddingLocation(false);
    } catch (error) {
      console.error('Error adding location:', error);
      toast({
        title: "Failed to add location",
        description: "There was an error adding the location. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateLocation = async (updatedLocation: Location) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/api/update_location`, {
        method: 'POST',
        headers: {
          'Accept' : 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updatedLocation)
      });

      if (!response.ok) {
        throw new Error('Failed to update location');
      }

      // Refresh locations
      fetchLocations();
      
      toast({
        title: "Location updated",
        description: `${updatedLocation.name} has been updated`
      });
      
      setEditingLocation(null);
    } catch (error) {
      console.error('Error updating location:', error);
      toast({
        title: "Failed to update location",
        description: "There was an error updating the location. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteLocation = async (id: string) => {
  const location = locations.find(location => location.id === id);
  if (location) {
    setLocationToDelete(location);
    setIsDeleteDialogOpen(true);
  }
};

  const confirmDeleteLocation = async () => {
  if (!locationToDelete) return;

  try {
    const response = await fetch(`${BASE_URL}/api/delete_location`, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ id: locationToDelete.id })
    });

    if (!response.ok) {
      throw new Error('Failed to delete location');
    }

    fetchLocations();

    toast({
      title: "Location deleted",
      description: `${locationToDelete.name} has been removed from your locations list`
    });
  } catch (error) {
    console.error('Error deleting location:', error);
    toast({
      title: "Failed to delete location",
      description: "There was an error deleting the location. Please try again.",
      variant: "destructive"
    });
  } finally {
    setIsDeleteDialogOpen(false);
    setLocationToDelete(null);
  }
};


  // Filter locations based on search query
  const filteredLocations = locations.filter(location => 
    location.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const CardComponent = small ? React.Fragment : Card;
  const extraProps = small ? {} : { className: "mb-6" };

  return (
    <CardComponent {...extraProps}>
      {!small && (
        <CardHeader>
          <CardTitle className="text-xl flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            {t('locations.locations')}
          </CardTitle>
        </CardHeader>
      )}
      <CardContent className={small ? "p-0" : ""}>
       <div className="flex justify-between items-center mb-4">
          <div className="relative w-48">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder={t('locations.search_locations')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8"
            />
          </div>
          <Button
            onClick={() => setIsAddingLocation(true)}
            className="gap-1 ml-2"
            disabled={isLoading}
          >
            <Plus className="h-4 w-4" />
            {!isMobile && t('locations.add_location')}
          </Button>
        </div>

        {/*isAddingLocation && (
          <div className="mb-4">
            <AddLocationForm 
              onSubmit={handleAddLocation}
              onCancel={() => setIsAddingLocation(false)}
            />
          </div>
        )*/}

        {isLoading ? (
          <div className="py-6 text-center text-sm">
            <Loader2 className="mr-2 h-4 w-4 animate-spin inline-block" />
          </div>
        ) : (
          <div className="space-y-2">
            {filteredLocations.map((location) => (
              <div
                key={location.id}
                className="flex items-center justify-between p-2 rounded-md bg-secondary/50"
              >

                <span className={`p-1 h-auto text-foreground ${location.isOwned ? 'hover:text-primary cursor-pointer' : ''} ${!isMobile ? 'font-medium' : 'text-sm'}`} onClick={() => location.isOwned ? setEditingLocation(location) : null}>
                  {location.name}
                  {!location.isOwned && (
                    <span className="ml-2 inline-flex items-center px-2 py-1 text-xs font-medium text-gray-800 bg-gray-200 rounded-full">
                      <MapPin className="h-3 w-3 mr-1" /> {t('locations.school_location')}
                    </span>
                  )}
                </span>

                {location.isOwned && (
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setEditingLocation(location)}
                      className="h-8 w-8 text-muted-foreground hover:text-foreground"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteLocation(location.id)}
                      className="h-8 w-8 text-destructive hover:text-destructive"
                    >
                      <Trash className="h-4 w-4" />
                    </Button>
                  </div>
                )}

              </div>
            ))}
            {filteredLocations.length === 0 && (
              <div className="text-center p-4 text-muted-foreground">
                {searchQuery ? "No locations found matching your search" : "No locations added yet"}
              </div>
            )}
          </div>
        )}
      </CardContent>


      <Dialog open={isAddingLocation} onOpenChange={(open) => !open && setIsAddingLocation(false)}>
        <DialogContent className={isMobile ? "p-0 w-full h-[100dvh] max-h-[100dvh]" : "max-w-[600px]"}>
          <DialogHeader className={isMobile ? "px-6 py-4 border-b mt-1" : "pb-5"}>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5 text-primary" />
              {t('locations.create_new')}
            </DialogTitle>
          </DialogHeader>
          <div className='flex-1 overflow-auto p-2'>
            <AddLocationForm
              onSubmit={handleAddLocation}
              onCancel={() => setIsAddingLocation(false)}
            />
          </div>
        </DialogContent>
      </Dialog>


      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('locations.confirmDelete')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('locations.confirmDeleteLocation')} "{locationToDelete?.name}"?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className='mt-5'>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteLocation}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>


      <Dialog open={!!editingLocation} onOpenChange={(open) => !open && setEditingLocation(null)}>
       <DialogContent className={isMobile ? "w-full h-[100dvh] max-h-[100dvh] flex flex-col" : "max-w-[600px] max-h-[80vh] flex flex-col"}>
          <DialogHeader className={isMobile ? "px-6 py-4 border-b mt-1" : "pb-5"}>
             <div className="flex items-center gap-2 font-medium text-lg">
              <MapPinHouseIcon className="h-5 w-5 text-blue-500" />
            <DialogTitle>{t('locations.create_new')}</DialogTitle>
            </div>
          </DialogHeader>
          {editingLocation && (
            <EditLocationForm
              location={editingLocation}
              onSubmit={handleUpdateLocation}
              onCancel={() => setEditingLocation(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </CardComponent>
  );
};

export default LocationsManager;
